export const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-6 md:p-8 shadow-lg border border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center border-b border-gray-200 pb-4">
            Terms and Conditions
          </h1>

          <div className="prose max-w-none">
            <p className="text-gray-700 mb-6 text-lg leading-relaxed">
              These Terms and Conditions ("Terms") govern your access to and use of the <strong className="text-blue-600">Busiconn</strong> platform. By using Busiconn, you agree to comply with and be bound by these Terms.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                User Responsibilities
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>You must provide accurate profile/business information and maintain account confidentiality.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>You are legally responsible for all activities conducted through your account.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Fraudulent, deceptive, or illegal activities are grounds for immediate termination.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Sellers must be at least 18 years old or legally eligible to enter into binding contracts in India.</span>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold bg-green-100 text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Rules for Buyers and General Users
              </h2>
              <ul className="space-y-3 text-gray-700 bg-green-200 p-2">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Do not share your phone number, WhatsApp, email, or any personal contact information in chats or live streams.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Always complete payments through the Busiconn platform to receive escrow protection.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Do not request sellers to transact outside the platform (e.g., UPI, direct bank transfers).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Buyers who repeatedly violate these terms may be suspended or permanently banned.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Respect community conduct standards during live sessions, including no abusive messages or spam.</span>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                B2B Transactions
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>All business posts must clearly describe goods/services, pricing, and delivery terms.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Users are solely responsible for contract fulfillment between buyers and sellers.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Busiconn acts only as a platform facilitator, not a party to transactions or deliveries.</span>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Payments, Escrow & Refunds
              </h2>
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-blue-600">•</span>
                    <strong>Escrow:</strong>
                  </h3>
                  <p className="ml-6 text-gray-700">Payments are held securely until buyer confirms satisfactory delivery/completion.</p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <strong>Refund Eligibility:</strong>
                  </h3>
                  <p className="ml-6 text-gray-700 mb-3">Buyers may request refunds for:</p>
                  <ul className="ml-10 space-y-2">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-green-600 mt-1.5 text-sm">◦</span>
                      <span>Undelivered goods/services</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-green-600 mt-1.5 text-sm">◦</span>
                      <span>Materially defective/non-conforming items</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-green-600 mt-1.5 text-sm">◦</span>
                      <span>Seller breach of contract terms</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start gap-2">
                    <span className="text-blue-600 mt-1">•</span>
                    <strong>Dispute Resolution:</strong>
                  </h3>
                  <ul className="ml-6 space-y-2">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-yellow-600 mt-1.5 text-sm">◦</span>
                      <span>Raise disputes within <strong className="text-blue-600">24 hrs</strong> of delivery via the platform</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-yellow-600 mt-1.5 text-sm">◦</span>
                      <span>Provide evidence (photos, contracts, chat logs)</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-yellow-600 mt-1.5 text-sm">◦</span>
                      <span>Busiconn will mediate and decide within <strong className="text-blue-600">3 business days</strong></span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-yellow-600 mt-1.5 text-sm">◦</span>
                      <span>Refunds processed via original payment method within <strong className="text-blue-600">5–10 business days</strong> post-resolution</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="text-blue-600">•</span>
                    <strong>Chargebacks:</strong>
                  </h3>
                  <p className="ml-6 text-gray-700">Unauthorized chargebacks may result in account suspension.</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Limitations for Skill & Job Posters
              </h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Jobs and skills listings must not mislead or exploit users.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>No commission-based job offers without transparent terms.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span>Busiconn may request KYC verification for repeated recruiters.</span>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Limitation of Liability
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-700 mb-3 flex items-start gap-3">
                    <span className="text-blue-600 mt-1.5 font-bold">•</span>
                    <span>Busiconn is not liable for:</span>
                  </p>
                  <ul className="ml-6 space-y-2">
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-orange-600 mt-1.5 text-sm">◦</span>
                      <span>Product quality or service outcomes</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-orange-600 mt-1.5 text-sm">◦</span>
                      <span>Shipping delays or third-party failures</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-700">
                      <span className="text-orange-600 mt-1.5 text-sm">◦</span>
                      <span>Financial losses from deals gone wrong</span>
                    </li>
                  </ul>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1.5 font-bold">•</span>
                  <span className="text-gray-700">Maximum liability is limited to the transaction amount in dispute.</span>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Termination
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We may suspend or terminate accounts for:
              </p>
              <div className="ml-4 space-y-2">
                <div className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold">(1)</span>
                  <span className="text-gray-700">breach of these Terms</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold">(2)</span>
                  <span className="text-gray-700">off-platform dealings</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold">(3)</span>
                  <span className="text-gray-700">repeated disputes or fraud</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-600 font-bold">(4)</span>
                  <span className="text-gray-700">contact information sharing in violation of our community safety policy</span>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Governing Law
              </h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by Indian law. Disputes shall be resolved through arbitration in New Delhi, India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-6 pb-2 border-b border-gray-300">
                Contact
              </h2>
              <p className="text-gray-700 mb-4">
                For disputes or questions:
              </p>
              <div className="bg-gray-100 rounded-lg p-6 border border-gray-200">
                <div className="text-gray-800 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📧</span>
                    <span>Email: <span className="text-blue-600 font-medium">contact@busiconn.com</span></span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📞</span>
                    <span>Phone: <span className="text-blue-600 font-medium">+91-9110067500</span></span>
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-sm mt-6 text-center italic border-t border-gray-200 pt-4">
                Last Updated: {new Date().toLocaleDateString("en-IN")}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
